import React, { Component } from 'react'

import classNames from 'classnames'
import animateScrollTo from 'animated-scroll-to'

import uiBox, { DecoratorProps } from 'client/shared/decorators/ui-box'

import Spacer from 'client/shared/blocks/spacer'

import {
  rgbToHSL,
  hexToRGB,
  lowerLightnessHSL,
  stringifyHSL,
} from 'client/shared/helpers/color-helpers'

import { EmotionProps, EmotionRatingProps } from 'client/shared/types/emotion'
import { ResourceProps } from 'client/shared/types/resource'

import './emotion-rating.styl'

const ENTER_CODE = 13

type Props = {
  emotionRating: EmotionRatingProps[]
  resource?: ResourceProps // should be passed whenever we want to render a bar chart
  kind: 'large' | 'medium' | 'small'
  isCard: boolean
} & DecoratorProps

class EmotionRating extends Component<Props> {
  static defaultProps = {
    kind: 'small',
    isCard: false,
  }

  scrollToLargeRating = () => {
    const large = document.querySelector('.list__impressions')
    if (large) {
      animateScrollTo(large.getBoundingClientRect().top - 88, { speed: 300 }) // 64 - size of header, 24 - top offset
    }
  }

  keydownHandler = evt => {
    if (evt.keyCode === ENTER_CODE) {
      this.scrollToLargeRating()
    }
  }

  render(): JSX.Element | null {
    const { emotionRating, kind, isCard } = this.props

    if (!emotionRating.length) return null

    const classes = classNames({
      'emotion-rating': true,
      [`emotion-rating_${kind}`]: true,
      'emotion-rating_card': isCard,
    })

    switch (kind) {
      case 'medium':
        return this.renderMedium(classes)
      case 'large':
        return this.renderLarge(classes)
      default:
        return this.renderSmall(classes)
    }
  }

  renderLarge(classes) {
    const { emotionRating, resource } = this.props

    if (!resource) return null
    const totalCount = resource.impressions_count

    return (
      <div className={classes}>
        <ul className="emotion-rating__list">
          {emotionRating.map((rating, index) => {
            const { label, color } = rating.emotion
            const countPercent = (rating.count / totalCount) * 100

            return (
              <li className="emotion-rating__item" key={index}>
                <div className="emotion-rating__info">
                  {this.renderEmotionImage(rating.emotion)}
                  {label && (
                    <span className="emotion-rating__label">{label}</span>
                  )}
                  <span className="emotion-rating__count">{rating.count}</span>
                </div>
                <Spacer size={8} />
                <div className="emotion-rating__status">
                  <div
                    className="emotion-rating__status-full"
                    style={{
                      backgroundColor: `#${color.toString(16)}`,
                      opacity: 0.3,
                    }}
                  />
                  <div
                    className="emotion-rating__status-fill"
                    style={{
                      backgroundColor: `#${color.toString(16)}`,
                      width: `${countPercent}%`,
                    }}
                  />
                </div>
                {index !== emotionRating.length - 1 && <Spacer size={24} />}
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  getColors(hex: string): string {
    const rgb = hexToRGB(hex)

    if (!rgb) return ''

    const { r, g, b } = rgb
    const hsl = rgbToHSL({ r, g, b })

    const { h, s, l } = hsl
    const resultingL = lowerLightnessHSL(l, 0.2)

    if (!resultingL) return ''

    const resultingHSL = stringifyHSL({ h, s, l: resultingL })

    if (!resultingHSL) return ''

    return resultingHSL
  }

  renderMedium(classes) {
    const { emotionRating, resource } = this.props

    if (!resource) return null

    const totalCount = resource.impressions_count

    return (
      <div
        className={classes}
        onClick={this.scrollToLargeRating}
        onKeyDown={this.keydownHandler}
        tabIndex={0}
      >
        <ul className="emotion-rating__list">
          {emotionRating.map((rating, index) => {
            const { label, color } = rating.emotion
            const countPercent = (rating.count / totalCount) * 100

            if (!color) return null

            return (
              <li className="emotion-rating__item" key={index}>
                <div className="emotion-rating__info">
                  {this.renderEmotionImage(rating.emotion)}
                  {label && (
                    <span className="emotion-rating__label">{label}</span>
                  )}
                  <span className="emotion-rating__count">{rating.count}</span>
                </div>
                <Spacer size={8} />
                <div className="emotion-rating__status">
                  <div
                    className="emotion-rating__status-full"
                    style={{
                      backgroundColor: this.getColors(color.toString(16)),
                      opacity: 0.3,
                    }}
                  />
                  <div
                    className="emotion-rating__status-fill"
                    style={{
                      backgroundColor: this.getColors(color.toString(16)),
                      width: `${countPercent}%`,
                    }}
                  />
                </div>
                {index !== emotionRating.length - 1 && <Spacer size={24} />}
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  renderSmall(classes) {
    const { emotionRating } = this.props

    return (
      <div
        className={classes}
        onClick={this.scrollToLargeRating}
        onKeyDown={this.keydownHandler}
        tabIndex={0}
      >
        <ul className="emotion-rating__list">
          {emotionRating.map((rating, index) => {
            return (
              <li className="emotion-rating__item" key={index}>
                {this.renderEmotionImage(rating.emotion)}
                <span className="emotion-rating__count">{rating.count}</span>
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  renderEmotionImage(emotion: EmotionProps) {
    const { kind, isWinPhone, isWindows } = this.props

    if (isWinPhone() || isWindows()) {
      return (
        <img
          className="emotion-rating__img"
          src={emotion.image_url}
          height="20"
          alt={emotion.label}
        />
      )
    } else {
      return (
        <span
          className="emotion-rating__img"
          title={kind === 'small' ? emotion.label : ''}
        >
          {emotion.unicode}
        </span>
      )
    }
  }
}

export default uiBox(EmotionRating)
